import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaExchangeAlt,
  FaArrowLeft,
  FaChartLine,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaBalanceScale,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaHistory,
  FaPlay,
} from "react-icons/fa";
import { getPnLSummary, getAccounts, postPnLTransfer, getPnLTransferHistory } from "./pnlTransferService";
import "./PnLTransfer.css";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const PnLTransferPage = () => {
  const navigate = useNavigate();

  // ── Filter state ─────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ── P&L data ─────────────────────────────────────────────
  const [pnlSummary, setPnlSummary] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlLoaded, setPnlLoaded] = useState(false);

  // ── Accounts ─────────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);

  // ── Form fields ───────────────────────────────────────────
  const [retainedEarningsAccount, setRetainedEarningsAccount] = useState("");
  const [pnlAccount, setPnlAccount] = useState("Profit & Loss Account");
  const [description, setDescription] = useState("");

  // ── Transfer state ────────────────────────────────────────
  const [transferring, setTransferring] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ── History ───────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // ── Load accounts & history on mount ─────────────────────
  useEffect(() => {
    loadAccounts();
    loadHistory();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await getAccounts();
      setAccounts(data || []);
      // Pre-select the first Equity account if available
      const equityAcc = data.find(
        (a) => a.account_type === "Equity" || a.account_name?.toLowerCase().includes("retained")
      );
      if (equityAcc) setRetainedEarningsAccount(equityAcc.account_name);
    } catch (e) {
      console.error("Failed to load accounts:", e);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await getPnLTransferHistory();
      setHistory(data || []);
    } catch (e) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Load P&L ─────────────────────────────────────────────
  const handleLoadPnL = async () => {
    setPnlLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date:   endDate   || undefined,
      };
      const data = await getPnLSummary(filters);
      setPnlSummary(data.summary || null);
      setPnlLoaded(true);
      // Auto-fill description
      const from = startDate || "start";
      const to   = endDate   || "end";
      setDescription(
        `P&L Transfer — Closing entries for period ${from} to ${to}`
      );
    } catch (e) {
      setErrorMsg("Failed to load P&L summary. Please try again.");
    } finally {
      setPnlLoading(false);
    }
  };

  // ── Execute Transfer ──────────────────────────────────────
  const handleTransfer = async () => {
    if (!pnlSummary) return;
    if (!retainedEarningsAccount.trim()) {
      setErrorMsg("Please select a Retained Earnings / Equity account.");
      return;
    }
    setTransferring(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const result = await postPnLTransfer({
        start_date: startDate || null,
        end_date:   endDate   || null,
        retained_earnings_account: retainedEarningsAccount,
        pnl_account:  pnlAccount,
        description:  description || "P&L Transfer — Closing Entry",
      });
      setSuccessMsg(
        `Transfer posted successfully! Journal Entry #${result.journal_id} created.`
      );
      setPnlLoaded(false);
      setPnlSummary(null);
      loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Transfer failed. Please try again.";
      setErrorMsg(detail);
    } finally {
      setTransferring(false);
    }
  };

  const netProfit = pnlSummary ? Number(pnlSummary.net_profit || 0) : 0;
  const isProfit  = netProfit >= 0;

  // Journal preview lines
  const journalLines = pnlLoaded && pnlSummary
    ? isProfit
      ? [
          { account: pnlAccount,             side: "Dr", amount: Math.abs(netProfit) },
          { account: retainedEarningsAccount || "Retained Earnings", side: "Cr", amount: Math.abs(netProfit) },
        ]
      : [
          { account: retainedEarningsAccount || "Retained Earnings", side: "Dr", amount: Math.abs(netProfit) },
          { account: pnlAccount,             side: "Cr", amount: Math.abs(netProfit) },
        ]
    : [];

  return (
    <div className="plt-page">
      {/* ── Header ────────────────────────────────────────── */}
      <div className="plt-header">
        <div className="plt-title-group">
          <h2>
            <FaExchangeAlt style={{ color: "#6366f1" }} />
            Profit &amp; Loss Transfer
          </h2>
          <p>Close P&amp;L balances to Retained Earnings / Equity at period end</p>
        </div>
        <div className="plt-header-actions">
          <button className="plt-btn plt-btn-back" onClick={() => navigate("/dashboard")}>
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Info banner ───────────────────────────────────── */}
      <div className="plt-info">
        <FaInfoCircle style={{ marginTop: 2, flexShrink: 0 }} />
        <span>
          The P&amp;L Transfer closes revenue and expense balances at period end by posting a
          journal entry that moves the net profit (or loss) into your Retained Earnings /
          Equity account. This is a standard accounting closing procedure.
        </span>
      </div>

      {/* ── Alerts ────────────────────────────────────────── */}
      {successMsg && (
        <div className="plt-success">
          <FaCheckCircle /> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="plt-error">
          <FaExclamationTriangle /> {errorMsg}
        </div>
      )}

      {/* ── Form Panel ────────────────────────────────────── */}
      <div className="plt-form-panel">
        <h3><FaChartLine style={{ color: "#6366f1" }} /> Step 1 — Select Period &amp; Load P&amp;L</h3>

        <div className="plt-form-grid">
          <div className="plt-field">
            <label>Period Start Date</label>
            <input
              id="plt-start-date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPnlLoaded(false); }}
            />
          </div>
          <div className="plt-field">
            <label>Period End Date</label>
            <input
              id="plt-end-date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPnlLoaded(false); }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              className="plt-btn plt-btn-load"
              onClick={handleLoadPnL}
              disabled={pnlLoading}
            >
              {pnlLoading ? <><span className="plt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Loading…</> : <><FaPlay /> Load P&amp;L</>}
            </button>
          </div>
        </div>

        {/* ── P&L Summary Cards ─────────────────────────── */}
        {pnlLoaded && pnlSummary && (
          <>
            <div className="plt-summary-grid" style={{ marginBottom: 24 }}>
              <div className="plt-card revenue">
                <div className="plt-card-icon" style={{ color: "#10b981" }}><FaMoneyBillWave /></div>
                <div className="plt-card-label">Total Revenue</div>
                <div className="plt-card-value">AED {fmt(pnlSummary.total_revenue)}</div>
              </div>
              <div className="plt-card cost">
                <div className="plt-card-icon" style={{ color: "#f59e0b" }}><FaFileInvoiceDollar /></div>
                <div className="plt-card-label">Total Cost</div>
                <div className="plt-card-value">AED {fmt(pnlSummary.total_cost)}</div>
              </div>
              <div className="plt-card gross">
                <div className="plt-card-icon" style={{ color: "#6366f1" }}><FaChartLine /></div>
                <div className="plt-card-label">Gross Profit</div>
                <div className="plt-card-value">AED {fmt(pnlSummary.gross_profit)}</div>
              </div>
              <div className="plt-card expense">
                <div className="plt-card-icon" style={{ color: "#ef4444" }}><FaBalanceScale /></div>
                <div className="plt-card-label">Expenses</div>
                <div className="plt-card-value">AED {fmt(pnlSummary.expenses)}</div>
              </div>
              <div className="plt-card net">
                <div className="plt-card-icon" style={{ color: isProfit ? "#10b981" : "#ef4444" }}>
                  <FaExchangeAlt />
                </div>
                <div className="plt-card-label">Net {isProfit ? "Profit" : "Loss"}</div>
                <div
                  className="plt-card-value"
                  style={{ color: isProfit ? "#16a34a" : "#dc2626" }}
                >
                  AED {fmt(Math.abs(netProfit))}
                </div>
                <div className="plt-card-sub">{isProfit ? "Profitable period" : "Loss period"}</div>
              </div>
            </div>

            {/* ── Step 2: Transfer Config ──────────────── */}
            <h3 style={{ marginBottom: 16 }}>
              <FaExchangeAlt style={{ color: "#10b981" }} /> Step 2 — Configure Transfer
            </h3>

            <div className="plt-form-grid">
              <div className="plt-field">
                <label>P&amp;L Account (Source)</label>
                <input
                  id="plt-pnl-account"
                  type="text"
                  value={pnlAccount}
                  onChange={(e) => setPnlAccount(e.target.value)}
                  placeholder="e.g. Profit & Loss Account"
                />
              </div>
              <div className="plt-field">
                <label>Retained Earnings / Equity Account (Destination)</label>
                {accounts.length > 0 ? (
                  <select
                    id="plt-retained-account"
                    value={retainedEarningsAccount}
                    onChange={(e) => setRetainedEarningsAccount(e.target.value)}
                  >
                    <option value="">— Select Account —</option>
                    {accounts
                      .filter((a) =>
                        ["Equity", "Liability"].includes(a.account_type) ||
                        a.account_name?.toLowerCase().includes("retained") ||
                        a.account_name?.toLowerCase().includes("equity")
                      )
                      .map((a) => (
                        <option key={a.id} value={a.account_name}>
                          {a.account_code} — {a.account_name}
                        </option>
                      ))}
                    <optgroup label="All Accounts">
                      {accounts.map((a) => (
                        <option key={`all-${a.id}`} value={a.account_name}>
                          {a.account_code} — {a.account_name} ({a.account_type})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                ) : (
                  <input
                    id="plt-retained-account-text"
                    type="text"
                    value={retainedEarningsAccount}
                    onChange={(e) => setRetainedEarningsAccount(e.target.value)}
                    placeholder="e.g. Retained Earnings"
                  />
                )}
              </div>
              <div className="plt-field" style={{ gridColumn: "1 / -1" }}>
                <label>Journal Description</label>
                <textarea
                  id="plt-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description for the closing journal entry…"
                />
              </div>
            </div>

            {/* ── Journal Preview ─────────────────────── */}
            {journalLines.length > 0 && (
              <div className="plt-preview">
                <h4>Journal Entry Preview</h4>
                <table className="plt-journal-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Dr / Cr</th>
                      <th className="num">Amount (AED)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journalLines.map((line, i) => (
                      <tr key={i}>
                        <td>{line.account || <em style={{ color: "#94a3b8" }}>Select account above</em>}</td>
                        <td>
                          <span className={line.side === "Dr" ? "plt-dr" : "plt-cr"}>
                            {line.side}
                          </span>
                        </td>
                        <td className="num">{fmt(line.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ margin: "12px 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                  This journal entry will{" "}
                  <strong>{isProfit ? "debit" : "credit"}</strong> the P&amp;L account and{" "}
                  <strong>{isProfit ? "credit" : "debit"}</strong> the Retained Earnings account
                  for AED {fmt(Math.abs(netProfit))}.
                </p>
              </div>
            )}

            {/* ── Transfer Button ──────────────────────── */}
            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
              <button
                className={`plt-btn plt-btn-transfer${!isProfit ? " loss" : ""}`}
                onClick={handleTransfer}
                disabled={transferring || !retainedEarningsAccount}
              >
                {transferring
                  ? <><span className="plt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Posting…</>
                  : <><FaExchangeAlt /> Post P&amp;L Transfer</>}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Transfer History ──────────────────────────────── */}
      <div className="plt-history-card">
        <div className="plt-history-header">
          <strong><FaHistory style={{ marginRight: 6, color: "#6366f1" }} />Transfer History</strong>
          <span>{history.length} entr{history.length !== 1 ? "ies" : "y"}</span>
        </div>

        {historyLoading ? (
          <div className="plt-loading">
            <div className="plt-spinner" /> Loading history…
          </div>
        ) : history.length === 0 ? (
          <div className="plt-empty">
            No P&amp;L transfers have been posted yet.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="plt-history-table">
              <thead>
                <tr>
                  <th>Journal #</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="num">Net Amount (AED)</th>
                  <th>Dr Account</th>
                  <th>Cr Account</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.journal_id}>
                    <td style={{ fontWeight: 600, color: "#6366f1" }}>#{h.journal_id}</td>
                    <td>{fmtDate(h.entry_date)}</td>
                    <td style={{ fontSize: "0.83rem", color: "#475569" }}>{h.description}</td>
                    <td className="num">
                      <span className="plt-dr">{fmt(h.amount)}</span>
                    </td>
                    <td>{h.dr_account}</td>
                    <td>{h.cr_account}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PnLTransferPage;
